/**
 * Leaguemate Scouting — S2 value layer (FantasyCalc).
 *
 * FantasyCalc's public API is keyed directly by Sleeper `player_id`
 * (`player.sleeperId`), so there is NO crosswalk — the value tag drops
 * straight onto roster player_ids. We map the target league's format profile
 * (from S1's toFormatProfile) to the FC query params, fetch once through the
 * throttled+cached layer, and parse to a sleeperId → value-info map.
 *
 * Live response shape confirmed 2026-05-31 (fixture saved to
 * src/Scripts/sleeper-data/scout-audit-cache/fantasycalc_sf_10team_ppr1.json):
 *   [ { player: { sleeperId, position, name, maybeTeam, maybeAge },
 *       value, overallRank, positionRank }, ... ]
 * Real players carry a numeric-string sleeperId; PICK entries use "DP_…" ids
 * (harmless — they never match a roster player_id and are filtered downstream).
 */

import { scoutFetch } from "./scoutFetch.js";

export const FANTASYCALC_BASE = "https://api.fantasycalc.com/values/current";

/**
 * Map a league format profile → FantasyCalc query params.
 *   SUPER_FLEX slot present → numQbs=2 (else 1); total_rosters → numTeams;
 *   rec scoring → ppr; dynasty assumed for /scout's audience.
 */
export function toFantasyCalcParams(profile, { isDynasty = true } = {}) {
  return {
    isDynasty,
    numQbs:   (profile?.superFlex || 0) >= 1 ? 2 : 1,
    numTeams: profile?.teamCount || 12,
    ppr:      profile?.ppr ?? 1,
  };
}

export function buildFantasyCalcUrl(params) {
  const { isDynasty, numQbs, numTeams, ppr } = params;
  return `${FANTASYCALC_BASE}?isDynasty=${isDynasty}&numQbs=${numQbs}&numTeams=${numTeams}&ppr=${ppr}`;
}

/**
 * Parse the FC array into Map<sleeperId(string), {value, position,
 * overallRank, positionRank, name, percentile}>. `percentile` is the player's
 * value-rank percentile in [0,1] (overallRank 1 → ~1.0) — the "valuable
 * believer" leverage signal. Pure; no I/O.
 */
export function parseFantasyCalcValues(raw) {
  const arr = Array.isArray(raw) ? raw : [];
  const maxRank = arr.reduce((m, e) => Math.max(m, e?.overallRank || 0), 0) || 1;
  const map = new Map();
  for (const e of arr) {
    const pl = e?.player;
    const sid = pl?.sleeperId;
    if (sid == null) continue;
    const rank = e.overallRank ?? null;
    map.set(String(sid), {
      value:        e.value ?? null,
      position:     pl.position ?? null,
      overallRank:  rank,
      positionRank: e.positionRank ?? null,
      name:         pl.name ?? null,
      percentile:   rank ? Math.max(0, 1 - (rank - 1) / maxRank) : 0,
    });
  }
  return map;
}

/**
 * Fetch + cache the value map for a target format profile.
 * Cached through the IndexedDB layer keyed by the format params (so distinct
 * formats cache independently). Returns { params, valueMap }.
 */
export async function fetchValueMap(profile, opts = {}) {
  const params = toFantasyCalcParams(profile, opts);
  const url = buildFantasyCalcUrl(params);
  const cacheId = `fc_${params.numQbs}qb_${params.numTeams}t_ppr${params.ppr}_dyn${params.isDynasty ? 1 : 0}`;
  const raw = await scoutFetch(url, { cacheId, season: "values", dataType: "scout_fc_values" });
  return { params, valueMap: parseFantasyCalcValues(raw), available: Array.isArray(raw) && raw.length > 0 };
}
